/**
 * SiteNavbar — the site navbar and the decorations it takes from the route.
 *
 * This was `MarketingNavbar`, and the rename is the whole change: with
 * `PageShell` composing chrome from one table, ONE component renders the
 * navbar on every kind that has one, so a name that claimed one section was a
 * name that lied. Its header already said what it is — *"it IS page chrome: it
 * decides a navbar variant and a section link and renders the shell"*; that
 * argument is why `MarketingNavbar` was moved out of `features/` in the first
 * place, and it is why the listings omnibox has now followed it (the
 * `ListingsSearchBar` beside this file).
 *
 * ## Two different questions, deliberately answered in two places
 *
 * `PageShell`'s table answers *which BANDS a page renders* — navbar, footer,
 * tab bar. It is keyed on the manifest's `chrome:` because that is a per-route
 * POLICY decision. This file answers *what the navbar itself shows* — a
 * transparent variant over a hero, the portal slot the home page docks its
 * search into, a section link, the browse omnibox. Folding the second question
 * into the first would mint a chrome kind per navbar decoration (a `hub` for
 * marketing, another for browse, a third for the directories) while every one
 * of them renders the identical three bands.
 *
 * ## The path predicates
 *
 * `stripLocale` + the `@letsgethome/routes` builders, not the hand regexes
 * this file used to carry (`/^(\/[a-z]{2}(-[A-Za-z]{2,4})?)?\/blog(\/|$)/`).
 * `stripLocale` matches only REGISTERED locales, so `/xy/blog` is no longer a
 * blog page, and `isUnder` is segment-anchored, so `/blogging` is not one
 * either — the same boundary bug `prefix_covers` was written for on the Rust
 * side, and the same helper `MobileBottomNav` already uses to decide its
 * active tab.
 */
import { usePathname } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { stripLocale } from '@letsgethome/lib/weburl';
import { routes } from '@letsgethome/routes';

import { ListingsSearchBar } from './ListingsSearchBar';
import { NavbarShell, type NavbarWidth } from './NavbarShell';
import type { SectionLink } from './NavLinksClient';

/** `path` is `base` or a path SEGMENT below it — never a mere string prefix. */
function isUnder(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`);
}

export function SiteNavbar({ width }: { readonly width: NavbarWidth }) {
  const pathname = stripLocale(usePathname());
  const tBlog = useTranslations('blog');
  const tPartners = useTranslations('partners');

  // The home page is the hero page: a transparent navbar that solidifies on
  // scroll, and the portal slot `DockableHeroSearch` docks its search into.
  const isHomePage = pathname === routes.home();

  // The browse hubs and their facets — the omnibox belongs in the navbar's
  // centre slot there, which is what the deleted `listings/pages/layout.tsx`
  // hand-composed. Detail pages (`/listings/{slug}`) are not under either
  // builder, so they keep the plain navbar they have always had.
  const isListingsBrowse =
    isUnder(pathname, routes.listings.rent()) || isUnder(pathname, routes.listings.buy());

  let sectionLink: SectionLink | undefined;
  if (isUnder(pathname, routes.blog.index())) {
    sectionLink = { label: tBlog('title'), href: routes.blog.index() };
  } else if (isUnder(pathname, routes.partners.index())) {
    sectionLink = { label: tPartners('title'), href: routes.partners.index() };
  }

  return (
    <NavbarShell
      variant={isHomePage ? 'transparent' : 'solid'}
      width={width}
      enablePortalSlot={isHomePage}
      sectionLink={sectionLink}
    >
      {isListingsBrowse ? <ListingsSearchBar /> : undefined}
    </NavbarShell>
  );
}
